import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { Tooltip } from 'react-tooltip'
import { InfoIcon } from '@toasttab/buffet-pui-icons'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations/src'
import { SavingsBreakdown } from './types'

type SavingsTooltipProps = {
  breakdown: SavingsBreakdown
  totalSaved: number
}

const SavingsTooltip = ({ breakdown, totalSaved }: SavingsTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false) // Track if opened by click/keyboard
  const containerRef = useRef<HTMLDivElement>(null)

  // Get line items that have values > 0
  const lineItems = [
    {
      key: 'promo',
      label: t('order-confirmation.post-order.savings-tooltip.promo-code'),
      amount: breakdown.promoCodeAmount,
      formattedAmount: useFormatPrice(breakdown.promoCodeAmount || 0, true)
    },
    {
      key: 'loyalty',
      label: t('order-confirmation.post-order.savings-tooltip.loyalty-reward'),
      amount: breakdown.loyaltyRewardAmount,
      formattedAmount: useFormatPrice(breakdown.loyaltyRewardAmount || 0, true)
    },
    {
      key: 'delivery',
      label: t(
        'order-confirmation.post-order.savings-tooltip.delivery-savings'
      ),
      amount: breakdown.deliverySavingsAmount,
      formattedAmount: useFormatPrice(
        breakdown.deliverySavingsAmount || 0,
        true
      )
    }
  ].filter((item) => item.amount && item.amount > 0)

  const formattedTotalSaved = useFormatPrice(totalSaved, true)

  // Handle hover - only affects state if not pinned
  const handleMouseEnter = () => {
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    // Only close on mouse leave if not pinned open
    if (!isPinned) {
      setIsOpen(false)
    }
  }

  // Handle click - pins the tooltip open
  const handleClick = () => {
    if (isPinned) {
      // If already pinned, unpin and close
      setIsPinned(false)
      setIsOpen(false)
    } else {
      // Pin it open
      setIsPinned(true)
      setIsOpen(true)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick() // Use same logic as click
    }
    if (event.key === 'Escape' && isOpen) {
      setIsPinned(false)
      setIsOpen(false)
    }
  }

  // Close tooltip when clicking outside (only if pinned)
  useEffect(() => {
    if (!isOpen || !isPinned) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const tooltipElement = document.getElementById(
        'savings-breakdown-tooltip'
      )

      // Check if click is outside both the button and tooltip
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        tooltipElement &&
        !tooltipElement.contains(target)
      ) {
        setIsPinned(false)
        setIsOpen(false)
      }
    }

    // Add listener after a small delay to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isPinned])

  // Close tooltip when focus leaves the component
  const handleBlur = (event: React.FocusEvent) => {
    // Check if the new focus target is outside the container
    const relatedTarget = event.relatedTarget as Node
    const tooltipElement = document.getElementById('savings-breakdown-tooltip')

    if (
      containerRef.current &&
      relatedTarget &&
      !containerRef.current.contains(relatedTarget) &&
      tooltipElement &&
      !tooltipElement.contains(relatedTarget)
    ) {
      setIsPinned(false)
      setIsOpen(false)
    }
  }

  // Don't render if there's only 1 or fewer savings methods
  if (lineItems.length <= 1) {
    return null
  }

  // Render tooltip content in a portal to avoid clipping by parent overflow
  const tooltipContent = (
    <Tooltip
      id='savings-breakdown-tooltip'
      anchorSelect='#savings-breakdown-info'
      data-testid='savings-tooltip-popup'
      place='bottom'
      isOpen={isOpen}
      clickable
      role='tooltip'
    >
      <div
        id='savings-breakdown-content'
        className='savingsTooltipContent'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role='region'
        aria-live='polite'
      >
        <strong className='savingsTooltipTitle'>
          {t('order-confirmation.post-order.savings-tooltip.breakdown-title')}
        </strong>
        <ul
          className='savingsTooltipList'
          aria-label={t(
            'order-confirmation.post-order.savings-tooltip.breakdown-title'
          )}
        >
          {lineItems.map((item) => (
            <li key={item.key} className='savingsTooltipListItem'>
              <span>{item.label}:</span> <span>{item.formattedAmount}</span>
            </li>
          ))}
        </ul>
        <strong className='savingsTooltipTotal'>
          {`${t(
            'order-confirmation.post-order.savings-tooltip.total-saved-label'
          )} ${formattedTotalSaved}`}
        </strong>
      </div>
    </Tooltip>
  )

  return (
    <>
      <div className='savingsTooltipContainer' ref={containerRef}>
        <button
          id='savings-breakdown-info'
          className='savingsTooltipButton'
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          aria-label={t(
            'order-confirmation.post-order.savings-tooltip.view-breakdown'
          )}
          aria-expanded={isOpen}
          aria-describedby={isOpen ? 'savings-breakdown-content' : undefined}
          type='button'
        >
          <InfoIcon accessibility='decorative' />
        </button>
      </div>
      {/* Render tooltip in a portal at document.body level to avoid overflow clipping */}
      {typeof document !== 'undefined' &&
        ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  )
}

export default SavingsTooltip
